import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';

import PropTypes from 'prop-types';

import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';

import { CircularLoaderComponent } from '../LoadingSpinner/LoadingSpinnerComponent';
import NotificationItemComponent from './NotificationItemComponent';
import NotificationsHeaderComponent from './NotificationsHeaderComponent';

import { HEADER_HEIGHT } from './constants';

const StyledContainer = styled(Box)(({ theme }) => ({
  width: '50rem',
  position: 'fixed',
  right: 0,
  top: theme.spacing(10),
  height: `calc(100vh - ${theme.spacing(10)})`,
  zIndex: theme.zIndex.drawer,
  backgroundColor: '#fff',
  border: '.1rem solid #CFCFCF',
  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
}));

const NotificationsComponent = (props) => {
  const {
    isViewed,
    getNextNotifications,
    markAllAsViewed,
    isLoading,
    notifications,
    hasNextPage,
    onDrawerClose,
  } = props;
  // If there are more items to be loaded then add an extra row to hold a loader.
  const itemCount = hasNextPage
    ? notifications.length + 1
    : notifications.length;

  // Only load 1 page of items at a time.
  // Pass an empty callback to InfiniteLoader in case it asks us to load more than once.
  const loadMoreItems = isLoading ? () => {} : getNextNotifications;

  // Every row is loaded except for a loader row (last one).
  const isItemLoaded = (index) => !hasNextPage || index < notifications.length;

  // Render an item or a loader.
  const ItemComponent = (value) => {
    const { data, index, style } = value;
    let content;
    if (!isItemLoaded(index)) {
      content = <CircularLoaderComponent />;
    } else {
      content = (
        <NotificationItemComponent
          data={data}
          index={index}
          isViewed={isViewed}
          onDrawerClose={onDrawerClose}
        />
      );
    }

    return <div style={style}>{content}</div>;
  };

  const getItemSize = (index) => {
    const currentNotification = notifications?.[index];
    const { subject, message } = currentNotification || {};
    if (!subject || !message) return 150;
    const lineHeight = 19;
    const titleHeight = Math.round(subject.length / 41) * lineHeight;
    const messageHeight = Math.round(message.length / 64) * lineHeight;
    return titleHeight + messageHeight + lineHeight * 3;
  };

  if (isLoading && !notifications.length) {
    return (
      <StyledContainer>
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularLoaderComponent />
        </Box>
      </StyledContainer>
    );
  }

  return (
    <StyledContainer>
      <NotificationsHeaderComponent markAllAsViewed={markAllAsViewed} />
      <Box
        sx={{
          width: '100%',
          height: `calc(100% - ${HEADER_HEIGHT}px)`,
          padding: '0 0 1rem 0.6rem',
        }}
      >
        <AutoSizer>
          {({ height, width }) => (
            <InfiniteLoader
              isItemLoaded={isItemLoaded}
              itemCount={itemCount}
              loadMoreItems={loadMoreItems}
            >
              {({ onItemsRendered, ref }) => (
                <VariableSizeList
                  height={height}
                  itemData={notifications}
                  itemCount={itemCount}
                  itemSize={getItemSize}
                  onItemsRendered={onItemsRendered}
                  ref={ref}
                  width={width}
                >
                  {ItemComponent}
                </VariableSizeList>
              )}
            </InfiniteLoader>
          )}
        </AutoSizer>
      </Box>
    </StyledContainer>
  );
};

NotificationsComponent.propTypes = {
  onDrawerClose: PropTypes.func.isRequired,
  getNextNotifications: PropTypes.func.isRequired,
  markAllAsViewed: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isViewed: PropTypes.bool.isRequired,
  hasNextPage: PropTypes.bool.isRequired,
  notifications: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      subject: PropTypes.string.isRequired,
      message: PropTypes.string.isRequired,
      createdAt: PropTypes.string.isRequired,
      viewed: PropTypes.bool.isRequired,
      metadata: PropTypes.shape({
        path: PropTypes.string,
        objectId: PropTypes.number,
      }),
    }),
  ).isRequired,
};

export default NotificationsComponent;
